import type { TransferTransportType } from '@bandido/shared/types';
import {
  PLACE_ID_PATTERN,
  PLACE_KINDS,
  ruteroMinTransferPickupDate,
} from '@bandido/shared/types';
import type { PlaceKind } from '@bandido/shared/types';
import type {
  RuteroBookingLegDirection,
  RuteroBookingLegMeetingPoint,
  RuteroBookingLegPickup,
} from '@bandido/shared/types';
import {
  isRuteroServiceTime,
  ruteroBookingLegMeetingPoint,
} from '@bandido/shared/types';

import { transferPickupView } from './transferPickup';
import type { TransferPickupView } from './transferPickup';

import { prettifyCode } from './format';
import { PricedSearchResult, TransfersSearchPayload } from './queries';
import { PlaceRef } from './places';
import { resolvedOfferRouteMapPoints } from './routeMap';
import {
  captureSearchQuote,
  SelectedTravelQuote,
} from './selectedQuote';

/**
 * Helpers del vertical de traslados.
 *
 * Conviven DOS proveedores con formas distintas de describir lo mismo:
 * ineedtours manda un `name` en prosa ("Traslado entre X y Y — Comfort Van") y
 * transferz manda la ruta resuelta más campos estructurados (categoría,
 * capacidad, valijas). Estos helpers normalizan lo justo para que las dos
 * ofertas se vean IGUAL en la lista — comparar es el punto de un fan-out.
 */

/**
 * Un tramo de la oferta, tal como lo publica el hub (`legs[]` de
 * `POST /rates/transfers/search`). `pickup` es el MISMO bloque que el del
 * booking, por eso reusa el tipo compartido en vez de declarar otro.
 */
export interface TransferOfferLeg {
  direction: RuteroBookingLegDirection;
  pickup?: RuteroBookingLegPickup;
  /** Fecha y hora de ENTREGA en destino, hora de pared, si el proveedor la publica. */
  deliveryLocalTime?: string;
  /** Punto e instrucciones estructurados; nunca se extraen de `conditions`. */
  meetingPoint?: RuteroBookingLegMeetingPoint;
}

/**
 * Identidad de una card dentro de una generación de búsqueda.
 *
 * La posición no identifica una oferta: filtrar u ordenar puede poner otra
 * oferta en el mismo índice y React le heredaría el estado local de la card
 * anterior (condiciones o punto de encuentro abiertos). La provenance sí es
 * única para la oferta cotizada y una generación nueva monta la cotización
 * nueva. React no materializa la key en el DOM.
 */
export function transferOfferListKey(
  offer: Pick<PricedSearchResult, 'provenance'>,
): string {
  return offer.provenance;
}

export type TransferOfferCoverage =
  | 'complete'
  | 'outbound-only'
  | 'return-only'
  | 'missing';

/**
 * Cobertura semántica de una oferta respecto del pedido. Una oferta legacy
 * de solo ida puede no publicar `legs`; un roundtrip, en cambio, falla cerrado
 * hasta demostrar ambos sentidos.
 */
export function transferOfferCoverage({
  isRoundTrip,
  legs,
}: {
  isRoundTrip: boolean;
  legs?: readonly Pick<TransferOfferLeg, 'direction'>[];
}): TransferOfferCoverage {
  if (!isRoundTrip) return 'complete';
  const hasOutbound = legs?.some((leg) => leg.direction === 'outbound') ?? false;
  const hasReturn = legs?.some((leg) => leg.direction === 'return') ?? false;
  if (hasOutbound && hasReturn) return 'complete';
  if (hasOutbound) return 'outbound-only';
  if (hasReturn) return 'return-only';
  return 'missing';
}

export function transferOfferBookable(input: {
  isRoundTrip: boolean;
  legs?: readonly Pick<TransferOfferLeg, 'direction'>[];
}): boolean {
  return transferOfferCoverage(input) === 'complete';
}

export const TRANSFER_OPERATING_DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;

export type TransferOperatingDay = (typeof TRANSFER_OPERATING_DAYS)[number];

export interface TransferOperatingHours {
  opensAt: string;
  closesAt: string;
  daysOfWeek: TransferOperatingDay[];
}

export interface TransferMeetingPointView {
  direction: RuteroBookingLegDirection;
  meetingPoint: RuteroBookingLegMeetingPoint;
}

/** Puntos de encuentro estructurados de la oferta, uno por tramo. */
export function transferOfferMeetingPointViews(
  offer: TransferOffer,
): TransferMeetingPointView[] {
  if (!Array.isArray(offer.legs)) return [];
  return offer.legs.flatMap((leg): TransferMeetingPointView[] => {
    if (leg?.direction !== 'outbound' && leg?.direction !== 'return') return [];
    const meetingPoint = ruteroBookingLegMeetingPoint(leg.meetingPoint);
    return meetingPoint ? [{ direction: leg.direction, meetingPoint }] : [];
  });
}

/**
 * Horarios declarados por el contrato, ordenando únicamente los días para
 * display. Ausencia no significa 24 h y un rango nocturno no se interpreta.
 */
export function transferOperatingHoursViews(value: unknown): TransferOperatingHours[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry): TransferOperatingHours[] => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return [];
    const raw = entry as Record<string, unknown>;
    if (!isRuteroServiceTime(raw.opensAt) || !isRuteroServiceTime(raw.closesAt)) {
      return [];
    }
    if (raw.opensAt === raw.closesAt && raw.opensAt !== '00:00') return [];
    if (!Array.isArray(raw.daysOfWeek)) return [];
    const uniqueDays = new Set(raw.daysOfWeek);
    if (uniqueDays.size !== raw.daysOfWeek.length) return [];
    const days = TRANSFER_OPERATING_DAYS.filter((day) => uniqueDays.has(day));
    if (days.length === 0 || days.length !== raw.daysOfWeek.length) return [];
    return [{ opensAt: raw.opensAt, closesAt: raw.closesAt, daysOfWeek: days }];
  });
}

/** Horarios del offer seleccionado que deben seguir visibles antes del hold. */
export function transferOperatingHoursForPreHoldDetail({
  vertical,
  hasBooking,
  value,
}: {
  vertical: string;
  hasBooking: boolean;
  value: unknown;
}): TransferOperatingHours[] {
  return vertical === 'transfers' && !hasBooking
    ? transferOperatingHoursViews(value)
    : [];
}

/** Un tramo de la oferta ya resuelto para dibujar. */
export interface TransferOfferLegView {
  direction: RuteroBookingLegDirection;
  pickup: TransferPickupView;
}

/**
 * Los tramos de la oferta que tienen una recogida que mostrar, en el orden en
 * que llegan. Un tramo con `unpublished` —o sin bloque— no produce vista: el
 * conector no publica el dato y no se inventa (nunca se cae a la hora que se
 * pidió, que es justamente la que puede estar a cuatro horas de distancia).
 *
 * Lista vacía = no hay nada que decir de los horarios de esta oferta.
 */
export function transferOfferLegViews(offer: TransferOffer): TransferOfferLegView[] {
  if (!Array.isArray(offer.legs)) return [];
  return offer.legs.flatMap((leg): TransferOfferLegView[] => {
    // La oferta no publica zona horaria: la hora va sin rótulo de zona, no
    // con una asumida.
    const pickup = transferPickupView(leg?.pickup);
    return pickup && (leg.direction === 'outbound' || leg.direction === 'return')
      ? [{ direction: leg.direction, pickup }]
      : [];
  });
}

/**
 * Una punta de la ruta, reducida a lo único que decide el rótulo de la hora.
 * `type` es el del pedido (`A` = aeropuerto); `placeKind` el del lugar
 * canónico elegido, que el payload no lleva pero el formulario sí conoce.
 */
export interface TransferRouteEnd {
  type?: string;
  placeKind?: string;
}

/**
 * ¿Alguna punta de la ruta es un AEROPUERTO?
 *
 * De esto depende cómo se rotula la hora que se pide: el contrato define esos
 * campos como "la hora conocida en cada punta", y en una ruta con aeropuerto
 * esa hora es la del VUELO (llegada si el traslado sale del aeropuerto,
 * salida si va hacia él). En un traslado hotel–hotel, puerto–hotel o entre
 * direcciones **no hay vuelo**, y llamarla así sería pedirle al asesor un
 * dato que no existe.
 *
 * Ante la duda —un lugar canónico del que no conocemos el tipo— devuelve
 * `false`: se rotula como hora del servicio, que es verdad siempre. Nunca se
 * afirma un vuelo sin evidencia.
 */
export function transferRouteTouchesAirport(
  ends: (TransferRouteEnd | undefined)[],
): boolean {
  return ends.some((end) => end?.type === 'A' || end?.placeKind === 'airport');
}

/**
 * Una punta del BORRADOR, a partir de lo que va a viajar y del lugar elegido.
 *
 * El `placeKind` solo cuenta **mientras hay un lugar elegido**: eso hace
 * estructuralmente imposible que sobreviva a una transición que abandona el
 * lugar (elegir carga manual, limpiar el campo, pasar a coordenadas). Sin
 * este gate, un aeropuerto canónico descartado seguía rotulando como vuelo un
 * punto tipeado a mano — §21.18.1: la invariante se completa, no se parchea
 * cada transición de a una.
 */
export function transferDraftRouteEnd(point: {
  type?: string;
  hasPlace: boolean;
  placeKind?: string;
}): TransferRouteEnd {
  return {
    ...(point.type ? { type: point.type } : {}),
    ...(point.hasPlace && point.placeKind ? { placeKind: point.placeKind } : {}),
  };
}

/**
 * Un hecho de recogida listo para rotular: la DECISIÓN (qué se dice y de qué
 * tramo) es pura y testeable; la tarjeta solo elige la clave de i18n.
 *
 * `direction` viaja SOLO cuando hay más de un tramo — con uno solo, decir
 * "Ida:" es ruido. `requested` viaja SOLO cuando difiere de la resuelta: es
 * la diferencia que hay que hacer notar, no un dato de adorno.
 */
export type TransferPickupFact =
  | {
    kind: 'resolved';
    direction?: RuteroBookingLegDirection;
    time: string;
    requested?: string;
  }
  | {
    kind: 'reconfirm';
    direction?: RuteroBookingLegDirection;
    /** Antelación mínima para reconfirmar, en horas, si el proveedor la publica. */
    minHoursBefore?: number;
    /** Dónde reconfirma el pasajero, ya saneada, si el proveedor la publica. */
    url?: string;
  }
  /**
   * El `pickUpTime` plano de una oferta SIN tramos (cacheada o anterior al
   * campo). No convive con los tramos: si el contrato publica `legs`, manda
   * él aunque no produzca ningún hecho — un `unpublished` es la afirmación
   * "este conector no publica la recogida", y caer ahí a la hora vieja
   * mostraría como hora del conductor una que el contrato declaró no
   * autoritativa.
   */
  | { kind: 'legacy'; time: string };

/**
 * Los hechos de recogida de una oferta, en el orden de sus tramos.
 *
 * `requestedTimes` son las horas del payload COMPROMETIDO de la búsqueda
 * (§21.23.4), nunca las del formulario en curso: con `keepPreviousData` el
 * form puede haber cambiado mientras estas ofertas siguen en pantalla.
 *
 * Un `resolved` sin hora (el proveedor sabe el día y todavía no la hora) no
 * produce hecho: en una línea de hechos un día suelto no dice nada, y el
 * detalle completo lo dibuja el post-booking.
 */
export function transferPickupFacts(
  offer: TransferOffer,
  requestedTimes?: { outbound?: string; return?: string },
): TransferPickupFact[] {
  const hasAuthoritativeLegs = Array.isArray(offer.legs) && offer.legs.length > 0;
  if (!hasAuthoritativeLegs) {
    return offer.pickUpTime ? [{ kind: 'legacy', time: offer.pickUpTime }] : [];
  }
  const legs = transferOfferLegViews(offer);
  /**
   * El sentido se decide con los tramos que el contrato PUBLICA, no con los
   * que sobreviven el filtro. Si un roundtrip trae la ida resuelta y la vuelta
   * `unpublished`, queda un solo hecho — y sin rótulo se leería como la
   * recogida del viaje entero, cuando es la de un tramo.
   */
  const withDirection = (offer.legs?.length ?? 0) > 1;
  return legs.flatMap((leg): TransferPickupFact[] => {
    const direction = withDirection ? { direction: leg.direction } : {};
    if (leg.pickup.status === 'reconfirm') {
      const { minHoursBefore, url } = leg.pickup.timeVerification ? {} : leg.pickup;
      return [
        {
          kind: 'reconfirm',
          ...direction,
          ...(minHoursBefore !== undefined ? { minHoursBefore } : {}),
          ...(url ? { url } : {}),
        },
      ];
    }
    const { time } = leg.pickup;
    if (!time) return [];
    const asked =
      leg.direction === 'return' ? requestedTimes?.return : requestedTimes?.outbound;
    return [
      {
        kind: 'resolved',
        ...direction,
        time,
        ...(asked && asked !== time ? { requested: asked } : {}),
      },
    ];
  });
}

export type TransferOffer = PricedSearchResult & {
  supportedAssociatedTransportTypes?: TransferTransportType[];
  name?: string;
  vehicleCategory?: string;
  /** Familia del servicio ("Traslados privados", "Shuttle compartido"). */
  category?: string;
  /**
   * Horas locales del punto, sin offset. HISTÓRICAS: describen un solo tramo
   * y el contrato manda leer `legs[].pickup`, que además distingue "todavía
   * no se sabe" de "no lo publica". Se mantienen como respaldo para ofertas
   * cacheadas de antes del campo.
   */
  pickUpTime?: string;
  deliveryTime?: string;
  /**
   * Los TRAMOS de la oferta con lo que el proveedor RESOLVIÓ de su horario
   * (roundtrip = 2). Es el dato que hace comparables dos ofertas: pedida la
   * misma hora, transferz recoge a esa hora y hotelbeds e ineedtours calculan
   * la recogida hacia atrás desde la hora del vuelo.
   *
   * El tramo de la OFERTA no es el del booking: acá no hay `id`, ni `state`,
   * ni zona horaria, ni categoría de vehículo. Lo único idéntico —y por eso
   * compartido— es `pickup`.
   */
  legs?: TransferOfferLeg[];
  /** Ventanas locales del servicio; ausente nunca equivale a 24 h. */
  operatingHours?: TransferOperatingHours[];
  /** Frases sueltas del operador: equipaje, cancelación, letra chica. */
  conditions?: string[];
  vehicleModels?: string;
  passengerCapacity?: number;
  luggageCapacity?: number;
  includedWaitingMinutes?: number;
  instantConfirmation?: boolean;
  freeCancellationUntil?: string;
  pricePerPassenger?: boolean;
};

/** Binds a re-quoted offer to the exact fetched cache generation atomically. */
export function captureTransferRequoteQuote(
  offer: PricedSearchResult,
  dataUpdatedAt: number | undefined,
  agencyId: string | number,
  payload: TransfersSearchPayload,
): SelectedTravelQuote<PricedSearchResult> | null {
  return captureSearchQuote(
    offer,
    dataUpdatedAt,
    'transfers',
    agencyId,
    payload,
  );
}

/**
 * Los campos ESTABLES que identifican el producto de una oferta de transfers
 * a través de generaciones de búsqueda. Es lo que el re-quote usa para volver
 * a encontrar "la misma oferta" en una respuesta nueva.
 */
export type TransferRequoteIdentitySource = Pick<
  TransferOffer,
  'provider' | 'name' | 'category' | 'vehicleCategory'
>;

/**
 * Identidad de PRODUCTO de una oferta para el re-quote, en un solo string
 * comparable.
 *
 * El `name` SOLO no alcanza: no es único entre proveedores (dos conectores
 * pueden publicar "AEROPUERTO → HOTEL X" para la misma ruta) y en un fan-out
 * la respuesta nueva puede venir reordenada — matchear por nombre podía
 * sustituir EN SILENCIO la oferta por la homónima de OTRO proveedor (otro
 * producto, otros términos, otro precio) conservando el formulario como si
 * fuera la misma. Por eso la identidad exige el `provider` y suma los
 * discriminantes de producto que el contrato publica y que no dependen del
 * horario (`category` — familia comercial — y `vehicleCategory`): el mismo
 * proveedor puede listar varias categorías bajo el mismo nombre de ruta.
 *
 * Quedan FUERA a propósito los campos que el re-quote legítimamente mueve
 * (horarios, precio, provenance — que además es opaco y por generación): la
 * identidad tiene que sobrevivir al cambio de horario, que es el punto del
 * flujo. `undefined` y `''` colapsan igual: la ausencia del rótulo no es un
 * discriminante.
 */
export function transferRequoteIdentity(
  offer: TransferRequoteIdentitySource,
): string {
  return JSON.stringify([
    offer.provider,
    offer.name ?? '',
    offer.category ?? '',
    offer.vehicleCategory ?? '',
  ]);
}

/**
 * Resultado de buscar la equivalente en una generación nueva. Tres estados
 * explícitos (§21.18.4), no un `T | undefined` que colapse dos motivos
 * distintos de "no sustituir":
 * - `unique`: EXACTAMENTE una oferta cumple la identidad — la única
 *   sustitución automática permitida.
 * - `not_found`: ninguna la cumple.
 * - `ambiguous`: más de una la cumple. La identidad tampoco es única POR
 *   CONTRATO: dos ofertas del MISMO proveedor pueden compartir proveedor +
 *   nombre + categoría + vehículo y diferir en modelo, capacidad, términos o
 *   precio — y el contrato no publica ningún id de producto estable entre
 *   búsquedas (la `provenance` es opaca y por generación; la oferta de
 *   transfers no trae campo id alguno). Elegir "la primera" reservaría un
 *   producto que el asesor no eligió.
 */
export type TransferRequoteMatch<T> =
  | { outcome: 'unique'; offer: T }
  | { outcome: 'not_found' }
  | { outcome: 'ambiguous' };

/**
 * La oferta EQUIVALENTE en una generación nueva, SOLO si es demostrablemente
 * única. Con 0 o con más de 1 candidata NO se sustituye nada: el caller
 * reporta el estado y el asesor elige explícitamente de la lista, que ya
 * quedó sembrada con la generación nueva.
 */
export function resolveTransferRequoteEquivalent<
  T extends TransferRequoteIdentitySource,
>(results: T[], current: TransferRequoteIdentitySource): TransferRequoteMatch<T> {
  const identity = transferRequoteIdentity(current);
  const matches = results.filter(
    (offer) => transferRequoteIdentity(offer) === identity,
  );
  if (matches.length === 1) return { outcome: 'unique', offer: matches[0] };
  return { outcome: matches.length === 0 ? 'not_found' : 'ambiguous' };
}

/**
 * A schedule re-quote starts from committed quote authority. Only the modal's
 * intentionally edited schedule may differ; dirty route/pax/date draft state
 * never enters this payload.
 */
export function transferRequotePayload(
  committedPayload: TransfersSearchPayload,
  newPickupTime: string,
  newReturnPickupTime?: string,
): TransfersSearchPayload {
  return {
    ...committedPayload,
    originTime: newPickupTime,
    ...(committedPayload.tripType === 'ROUNDTRIP'
      ? {
        destinationTime: newReturnPickupTime ?? committedPayload.destinationTime,
      }
      : {}),
  };
}

/** Lo que el buscador trae al re-cotizar: la generación y su timestamp. */
export interface TransferRequoteFetch {
  envelope: { results: PricedSearchResult[] };
  dataUpdatedAt: number | undefined;
}

/** Los efectos del re-quote, inyectados para que el flujo sea testeable. */
export interface TransferRequoteEffects {
  /** Trae SIEMPRE una generación nueva (el buscador fetchea con `staleTime: 0`). */
  fetchGeneration: (
    payload: TransfersSearchPayload,
  ) => Promise<TransferRequoteFetch>;
  /**
   * Convierte la generación traída en la búsqueda ACTIVA: horarios del
   * formulario + URL (de la que sale el payload commiteado, la query del
   * listado y la identidad de búsqueda activa).
   */
  commitSchedule: (payload: TransfersSearchPayload) => void;
}

/**
 * El flujo COMPLETO de un re-quote, con los efectos inyectados.
 *
 * Invariante (§21.18.1): la generación traída con ÉXITO pasa a ser la
 * búsqueda activa SIEMPRE — `commitSchedule` corre para todo fetch exitoso,
 * ANTES e independientemente del match. Los avisos de `not_found`/`ambiguous`
 * mandan a "elegir de la lista nueva", y esa promesa solo es verdad si la
 * lista visible ES la generación nueva: commitear solo en el camino feliz
 * dejaba la lista en el horario VIEJO justo cuando el asesor tenía que elegir
 * a mano — podía elegir una oferta del horario anterior creyendo que era del
 * nuevo. Lo ÚNICO que depende de la unicidad del match es la sustitución
 * automática del quote del modal (sin match único, el modal retiene su quote
 * — que queda correctamente bloqueado: ya no es de la búsqueda activa).
 *
 * Si el FETCH falla, no se commitea nada: no hay generación nueva que
 * mostrar y la lista vigente sigue siendo la verdad.
 */
export async function runTransferRequote(
  committedPayload: TransfersSearchPayload,
  currentOffer: TransferRequoteIdentitySource,
  agencyId: string | number,
  newTime: string,
  newReturnTime: string | undefined,
  effects: TransferRequoteEffects,
): Promise<SelectedTravelQuote<PricedSearchResult> | 'not_found' | 'ambiguous'> {
  const payload = transferRequotePayload(committedPayload, newTime, newReturnTime);
  const { envelope, dataUpdatedAt } = await effects.fetchGeneration(payload);
  effects.commitSchedule(payload);
  const match = resolveTransferRequoteEquivalent(envelope.results, currentOffer);
  if (match.outcome !== 'unique') return match.outcome;
  const quote = captureTransferRequoteQuote(
    match.offer,
    dataUpdatedAt,
    agencyId,
    payload,
  );
  return quote ?? 'not_found';
}

export interface TransferRoute {
  from: string;
  to: string;
}

export interface TransferRequestedRoutes {
  outbound: TransferRoute;
  return?: TransferRoute;
}

/**
 * El fallback del pedido para ESTA oferta. Un conector puede publicar cada
 * sentido del roundtrip como una oferta separada; la dirección contractual de
 * su único tramo decide si corresponde invertir los extremos. Sin esa
 * evidencia se usa la ida y no se adivina nada desde el nombre/proveedor.
 */
export function requestedTransferRoute(
  offer: TransferOffer,
  routes: TransferRequestedRoutes,
): TransferRoute {
  return offer.legs?.length === 1 &&
    offer.legs[0].direction === 'return' &&
    routes.return
    ? routes.return
    : routes.outbound;
}

/**
 * La ruta RESUELTA de la oferta. Sale exclusivamente de los dos extremos
 * estructurados del contrato. `name` es texto de presentación y jamás se
 * interpreta como protocolo para reconstruir puntos.
 */
export function transferRoute(offer: unknown): TransferRoute | null {
  const points = resolvedOfferRouteMapPoints('transfers', offer);
  if (points.length !== 2) return null;
  const from = points[0].label?.trim() || points[0].address?.trim();
  const to = points[1].label?.trim() || points[1].address?.trim();
  return from && to ? { from, to } : null;
}

export interface TransferRoutePresentation extends TransferRoute {
  source: 'resolved' | 'resolved-requested-labels' | 'requested';
}

/** Una sola decisión para lo que muestra la card y el rótulo de autoridad. */
export function transferRoutePresentation(
  offer: TransferOffer,
  requestedRoutes?: TransferRequestedRoutes,
): TransferRoutePresentation | null {
  const resolved = transferRoute(offer);
  if (resolved) return { ...resolved, source: 'resolved' };
  const hasResolvedRoute =
    resolvedOfferRouteMapPoints('transfers', offer).length === 2;
  if (!requestedRoutes) return null;
  return {
    ...requestedTransferRoute(offer, requestedRoutes),
    source: hasResolvedRoute ? 'resolved-requested-labels' : 'requested',
  };
}

/**
 * Título y categoría declarados por el contrato. `name` se conserva íntegro:
 * partir prosa con `—` convertiría un formato incidental del proveedor en una
 * regla propia de Bandido.
 */
export function transferTitle(offer: TransferOffer): {
  title: string;
  category?: string;
} {
  const category = offer.vehicleCategory?.trim() || offer.category?.trim();
  return {
    title: offer.name ?? '',
    ...(category ? { category } : {}),
  };
}

/** Categoría normalizada para filtrar (el texto varía entre proveedores). */
export function categoryKey(offer: TransferOffer): string {
  const { category } = transferTitle(offer);
  return (category ?? '').trim().toUpperCase();
}

/**
 * La key de filtro va normalizada en MAYÚSCULA porque los proveedores escriben
 * la categoría distinto; para mostrarla se humaniza.
 */
export const categoryLabel = prettifyCode;

export interface TransferFilters {
  categories: string[];
  providers: string[];
  minCapacity: number;
  instantOnly: boolean;
  freeCancellationOnly: boolean;
}

export const EMPTY_TRANSFER_FILTERS: TransferFilters = {
  categories: [],
  providers: [],
  minCapacity: 0,
  instantOnly: false,
  freeCancellationOnly: false,
};

export function filterTransfers(
  offers: TransferOffer[],
  filters: TransferFilters,
): TransferOffer[] {
  return offers.filter((offer) => {
    if (filters.providers.length && !filters.providers.includes(offer.provider)) {
      return false;
    }
    if (filters.categories.length && !filters.categories.includes(categoryKey(offer))) {
      return false;
    }
    if (filters.minCapacity) {
      // Sin capacidad declarada no se puede afirmar que entre la familia.
      const capacity = offer.passengerCapacity;
      if (typeof capacity !== 'number' || capacity < filters.minCapacity) return false;
    }
    if (filters.instantOnly && offer.instantConfirmation !== true) return false;
    if (filters.freeCancellationOnly && !offer.freeCancellationUntil) return false;
    return true;
  });
}

/**
 * Cuántas ofertas quedarían con ESTA opción puesta, respetando el resto de los
 * filtros y pisando la faceta propia (así se cuenta en búsqueda facetada).
 */
export function transferFacetCount(
  offers: TransferOffer[],
  filters: TransferFilters,
  patch: Partial<TransferFilters>,
): number {
  return filterTransfers(offers, { ...filters, ...patch }).length;
}

export type TransferSort = 'price' | 'capacity';

export function sortTransfers(
  offers: TransferOffer[],
  sort: TransferSort,
): TransferOffer[] {
  return [...offers].sort((a, b) => {
    if (sort === 'capacity') {
      const diff = (b.passengerCapacity ?? 0) - (a.passengerCapacity ?? 0);
      if (diff !== 0) return diff;
    }
    return a.pricing.final.amountMinor - b.pricing.final.amountMinor;
  });
}

/** Ícono a usar cuando el proveedor no manda foto del vehículo. */
export type TransferIcon = 'bus' | 'van' | 'car';

export function transferIcon(offer: TransferOffer): TransferIcon {
  const category = categoryKey(offer);
  if (/BUS|SHUTTLE|COACH/.test(category)) return 'bus';
  if (/VAN|MINIBUS|MINIVAN/.test(category)) return 'van';
  return 'car';
}

// ── Punto por lugar canónico (plc_*, #2178) ────────────────────────────────
// El punto de pickup/dropoff en modo IATA admite DOS submodos mutuamente
// excluyentes, igual que el resto del epic #2167: código IATA de 3 letras
// tipeado a mano, o un lugar de `PlaceAutocomplete`
// (`kinds: ['airport','station','port']`). Viajan en el MISMO param de URL
// (`${prefix}iata`, ver `TransfersSearchForm.tsx`) — la forma del valor
// (`plc_*` o no) decide cuál es, así que una URL de un submodo nunca
// reconstruye el otro con datos a medias.

/**
 * ÚNICA definición de la key del nombre del lugar en la URL (21.22) — el
 * nombre para MOSTRAR (nunca al payload: ver el doc de `pointLabel` en
 * `TransfersSearchForm.tsx`). Cada lugar que necesite leer o escribir este
 * param pasa por acá, no repite el template literal a mano: un `${prefix}iataNombre`
 * tecleado de nuevo en cada call-site es exactamente cómo un casing distinto
 * (`oIataNombre` vs. la key real `oiataNombre`) puede colarse sin que nada
 * lo note — el nombre del lugar elegido dejaría de mostrarse en el resumen
 * de la reserva y en las búsquedas recientes, en silencio, con el `plc_*`
 * crudo en su lugar.
 */
export function transferPointNameParam(prefix: 'o' | 'd'): string {
  return `${prefix}iataNombre`;
}

/**
 * Parámetro con el TIPO REAL del lugar canónico elegido (`airport`, `station`,
 * `port`...). Existe porque el rótulo de la hora depende de si la ruta toca un
 * aeropuerto, y sin persistirlo un link restaurado no puede saberlo: el `kind`
 * que reconstruye `transferPointPlaceFromParams` es display-only y está fijo
 * en `'airport'`, así que usarlo para decidir rotularía una ESTACIÓN canónica
 * como "hora del vuelo".
 *
 * Ausente (links viejos, o punto que no es lugar) = no se sabe, y entonces no
 * se afirma ningún vuelo.
 */
export function transferPointKindParam(prefix: 'o' | 'd'): string {
  return `${prefix}iataTipo`;
}

/** El tipo real del lugar que trae la URL, o nada si no es uno del maestro. */
export function transferPointKindFromParams(value: string | undefined): PlaceKind | undefined {
  return value && (PLACE_KINDS as readonly string[]).includes(value)
    ? (value as PlaceKind)
    : undefined;
}

/** Reconstruye el código IATA nativo: `''` si el valor tiene forma de lugar (`plc_*`). */
export function transferPointIataFromParams(iataParam: string): string {
  return PLACE_ID_PATTERN.test(iataParam) ? '' : iataParam;
}

/**
 * Reconstruye el lugar elegido: `null` si el valor NO tiene forma `plc_*`
 * (URL guardada en modo IATA nativo). Sin nombre en la URL (link recortado
 * a mano), el id sirve de nombre — mismo fallback que el resto del epic.
 * `kind` es fijo (`'airport'`): es display-only (no viaja al payload, que
 * solo usa el id) y nunca se vuelve a mostrar en un dropdown para un valor
 * ya elegido, así que no hace falta persistir el kind real en la URL.
 */
export function transferPointPlaceFromParams(
  iataParam: string,
  name: string | undefined,
): PlaceRef | null {
  if (!PLACE_ID_PATTERN.test(iataParam)) return null;
  const label = name ?? iataParam;
  return { id: iataParam, name: label, breadcrumb: label, kind: 'airport' };
}

/**
 * Los ocupantes MENORES que declara el parámetro `menores` de la URL
 * ("8,12"), en el orden en que se escribieron.
 *
 * Vive acá, junto a los otros helpers de parámetros del vertical, porque tiene
 * un caso de borde que costó un pasajero fantasma: sin menores el parámetro es
 * la cadena vacía, y `''.split(',')` da `['']`, cuyo `Number('')` es 0 — un
 * entero >= 0 que pasaba cualquier filtro de edad. Toda búsqueda de traslados
 * sin menores viajaba con un menor de 0 años, así que el proveedor cotizaba un
 * pasajero de más: otros vehículos, otro precio (#2374).
 */
export function transferChildOccupants(
  menores: string | undefined,
): { type: 'CHL'; age: number }[] {
  return (menores ?? '')
    .split(',')
    .map((age) => age.trim())
    .filter((age) => age !== '')
    .map((age) => Number(age))
    .filter((age) => Number.isInteger(age) && age >= 0)
    .map((age) => ({ type: 'CHL' as const, age }));
}

/**
 * Solo valores con esta forma se comparan como fecha. Un residuo de deep link
 * editado a mano ("garbage") no se marca como "pasado": esa forma la rechaza
 * `IsCalendarDate` en el borde, con su propio motivo.
 */
const CALENDAR_DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

export interface TransferScheduleValidation {
  /** `min` del input de ida — la MISMA cota que rechaza el DTO (§21.22). */
  minPickupDate: string;
  /** `min` del input de vuelta: nunca anterior a la ida ni a la cota. */
  minReturnDate: string;
  /** La ida ya pasó en TODAS las zonas (anterior a HOY en UTC−12). */
  pickupInPast: boolean;
  /** RT: la vuelta ya pasó (misma cota que la ida). */
  returnInPast: boolean;
  /** RT: la vuelta es anterior a la ida. */
  returnBeforePickup: boolean;
  /** Las fechas cargadas describen una búsqueda que el borde no rechaza. */
  ok: boolean;
}

/**
 * Validación de las fechas del formulario de traslados, con la MISMA cota que
 * el DTO compartido (`ruteroMinTransferPickupDate`: HOY en UTC−12 — el
 * criterio del hub para `PICKUP_IN_PAST`, y `endDate >= date` para
 * `RETURN_BEFORE_DEPARTURE`). Es la ÚNICA derivación del formulario: de acá
 * salen el `min` de los inputs, el error de cada campo y el gate del submit
 * (§21.9: lo que se valida y lo que se dibuja, de la misma expresión).
 *
 * En solo ida la vuelta residual no cuenta: no viaja en el payload.
 */
export function transferScheduleValidation(input: {
  date: string;
  endDate: string;
  isRoundTrip: boolean;
  /** Inyectable en tests; en la pantalla, el reloj real. */
  now?: Date;
}): TransferScheduleValidation {
  const minPickupDate = ruteroMinTransferPickupDate(input.now);
  const dateComparable = CALENDAR_DATE_SHAPE.test(input.date);
  const endComparable =
    input.isRoundTrip && CALENDAR_DATE_SHAPE.test(input.endDate);
  const pickupInPast = dateComparable && input.date < minPickupDate;
  const returnInPast = endComparable && input.endDate < minPickupDate;
  const returnBeforePickup =
    dateComparable && endComparable && input.endDate < input.date;
  return {
    minPickupDate,
    minReturnDate:
      dateComparable && input.date > minPickupDate ? input.date : minPickupDate,
    pickupInPast,
    returnInPast,
    returnBeforePickup,
    ok: !pickupInPast && !returnInPast && !returnBeforePickup,
  };
}
